import { PasswordInput } from "../password-input";
import { Button } from "../Button";
import { useState } from "react";
import { toast } from "react-toastify";
import { useStore } from "../../store";
import api from "../../service/api";
import { Lock, ShieldAlert } from "lucide-react";

export function MustChangePasswordModal() {
  const user = useStore((s) => s.user);
  const setUser = useStore((s) => s.setUser);
  const [provisionalFromSession] = useState(
    () => sessionStorage.getItem("@apcap:provisionalPwd") ?? ""
  );
  const [provisionalInput, setProvisionalInput] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user?.id || !user.must_change_password) {
    return null;
  }

  const effectiveOldPassword = provisionalFromSession || provisionalInput;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!effectiveOldPassword.trim()) {
      toast.error("Informe a senha provisória que você utilizou para entrar.");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("A confirmação não confere com a nova senha.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.patch("/users/update-pass", {
        id: user.id,
        old_password: effectiveOldPassword,
        new_password: newPassword,
      });
      const updated = { ...user, ...data, must_change_password: false };
      setUser(updated);
      localStorage.setItem("@apcap:user", JSON.stringify(updated));
      sessionStorage.removeItem("@apcap:provisionalPwd");
      setNewPassword("");
      setConfirm("");
      setProvisionalInput("");
      toast.success("Senha alterada com sucesso.");
    } catch {
      toast.error(
        "Não foi possível alterar a senha. Verifique a senha provisória."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="must-change-password-title"
    >
      <div className="absolute inset-0 bg-[#003b43]/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/35 bg-white shadow-2xl shadow-[#002428]/30 ring-1 ring-white/40">
        <div className="border-b border-[#015832]/12 bg-emerald-50/90 px-6 py-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-white p-2 ring-1 ring-[#015832]/15">
              <ShieldAlert className="h-6 w-6 text-[#015832]" />
            </div>
            <div>
              <h2
                id="must-change-password-title"
                className="text-lg font-semibold text-[#003b43]"
              >
                Defina uma nova senha
              </h2>
              <p className="mt-1 text-sm text-zinc-600">
                Você entrou com uma senha provisória. Por segurança, altere-a
                antes de continuar.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-6">
          {!provisionalFromSession ? (
            <div>
              <label
                htmlFor="provisional-pwd"
                className="mb-1.5 block text-sm font-medium text-zinc-800"
              >
                Senha provisória
              </label>
              <PasswordInput
                id="provisional-pwd"
                name="provisional"
                variant="auth"
                autoComplete="current-password"
                value={provisionalInput}
                onChange={(e) => setProvisionalInput(e.target.value)}
                placeholder="A senha recebida por e-mail"
              />
            </div>
          ) : null}

          <div>
            <label
              htmlFor="new-pwd"
              className="mb-1.5 flex items-center gap-2 text-sm font-medium text-zinc-800"
            >
              <Lock className="h-3.5 w-3.5 text-[#015832]" />
              Nova senha
            </label>
            <PasswordInput
              id="new-pwd"
              name="new_password"
              variant="auth"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
            />
          </div>

          <div>
            <label
              htmlFor="confirm-pwd"
              className="mb-1.5 block text-sm font-medium text-zinc-800"
            >
              Confirmar nova senha
            </label>
            <PasswordInput
              id="confirm-pwd"
              name="confirm"
              variant="auth"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repita a nova senha"
            />
          </div>

          <Button
            type="submit"
            variant="brand"
            className="w-full h-11 font-medium"
            disabled={submitting}
          >
            {submitting ? "Salvando…" : "Salvar nova senha"}
          </Button>
        </form>
      </div>
    </div>
  );
}
